import AddProductsHeader from "../../components/Products/AddProductsHeader";
import { useEffect, useState, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import SuccessPopUp from "../../components/Products/SuccessPopUp";
import ProductPhotos from "../../components/Products/ProductPhotos";
import ProductForm from "../../components/Products/ProductForm";
import { useSuppliers } from "../../hooks/useSuppliers";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;
const initialFormState = {
  name: "",
  name_ar: "",
  type: "",
  sku: "",
  description: "",
  description_ar: "",
  price: "",
  shipping_price: "",
  shipping_period: "",
  minimum_order: "",
  quantity: "",
  supplier_id: "",
  offer_type: "",
  active: true,
};

function AddProduct() {
  const { t } = useTranslation("addProductsHeader");
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [adding, setAdding] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isFormValid, setIsFormValid] = useState(false);
  const [imageFocus, setImageFocus] = useState({});
  const { isAuthenticated } = useAuth();
  // state for custom product 
  const [isCustomProduct, setIsCustomProduct] = useState(false);

  const {
    suppliers,
    loading: suppliersLoading,
    error: suppliersError,
  } = useSuppliers();

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  useEffect(() => {
    const requiredFields = [
      'name', 'name_ar', 'type', 'description', 'description_ar', 
      'supplier_id', 'offer_type', 'sku'
    ];
    
    // For non-custom products, also require price and commerce fields
    const commerceFields = ['price', 'shipping_period', 'minimum_order', 'quantity'];
    const fieldsToCheck = isCustomProduct ? requiredFields : [...requiredFields, ...commerceFields];
    
    const allFieldsFilled =
      images.length > 0 &&
      images.length <= 5 &&
      fieldsToCheck.every(field => {
        const value = formData[field];
        return String(value).trim() !== "" && value !== "0";
      }) &&
      formData.description.length >= 10 &&
      formData.name.length >= 3 &&
      formData.name_ar.length >= 3 &&
      formData.description_ar.length >= 10;

    setIsFormValid(allFieldsFilled);
  }, [formData, images, isCustomProduct]);

  const objectToFormData = (obj) => {
    const formData = new FormData();
    Object.entries(obj).forEach(([key, value]) => {
      formData.append(key, value);
    });
    images.forEach((image) => formData.append("images", image));
    return formData;
  };

  const postProduct = async (newProduct) => {
    if (adding) return;
    setAdding(true);

    const loadingToast = toast.loading(t("loadingToast"));
    if (!isAuthenticated) return;

    const formDataProduct = objectToFormData(newProduct);

    try {
      //POST

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-supplier/services/${isCustomProduct ? 'custom-create' : 'create'}`,
        {
          method: "POST",
          credentials: "include",
          headers: { lang: getLangFromLocalStorage() },
          body: formDataProduct,
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error("Unauthorized access. Please log in again.");
        }
        const errorData = await response.json();
        console.error("Error submitting product:", errorData);

        // Handle structured error response
        if (errorData.errors) {
          let errorMessages = "";

          if (Array.isArray(errorData.errors)) {
            // Handle array of error objects
            errorMessages = errorData.errors
              .map((errorObj) =>
                Object.entries(errorObj)
                  .map(([field, message]) => `${field}: ${message}`)
                  .join(", ")
              )
              .join(", ");
          } else {
            // Handle direct error object (your original code)
            errorMessages = Object.entries(errorData.errors)
              .map(
                ([field, messages]) =>
                  `${field}: ${
                    Array.isArray(messages) ? messages.join(", ") : messages
                  }`
              )
              .join(", ");
          }

          throw new Error(errorMessages);
        } else {
          // Handle simple error message
          throw new Error(
            errorData.error || errorData.message || t("unknownError")
          );
        }
      }

      toast.update(loadingToast, {
        render: <SuccessPopUp addOrEdit="added" />,
        type: "success",
        autoClose: 3000,
        icon: false,
        style: {
          width: "340px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
        },
      });

      setTimeout(() => navigate(-1), 3000);
    } catch (error) {
      setAdding(false);
      toast.dismiss(loadingToast);
      toast.error(error.message || t("submitError"), {
        autoClose: 5000,
        style: {
          whiteSpace: "pre-line", // Allow line breaks in error messages
        },
      });
      console.error("Error submitting product:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newProduct = {
      name: formData.name,
      name_ar: formData.name_ar,
      type: formData.type,
      sku: formData.sku,
      description: formData.description,
      description_ar: formData.description_ar,
      supplier_id: parseInt(formData.supplier_id),
      offer_type: formData.offer_type,
      active: formData.active,
      customized: isCustomProduct,
    };

    // Only add commerce fields for non-custom products
    if (!isCustomProduct) {
      newProduct.price = parseFloat(formData.price);
      newProduct.shipping_price = parseFloat(formData.shipping_price || 0);
      newProduct.shipping_period = formData.shipping_period;
      newProduct.minimum_order = parseInt(formData.minimum_order);
      newProduct.quantity = parseInt(formData.quantity);
    }

    await postProduct(newProduct);
  };

  if (suppliersLoading)
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  if (suppliersError) return <p>Error: {suppliersError}</p>;

  return (
    <div className="flex flex-col gap-10 my-16 mx-5">
      <AddProductsHeader 
      title={isCustomProduct ? t("customProduct") : t("standardProduct")} 
      hasDes={true} 
      productSide={true}
      isCustomProduct={isCustomProduct}
      setIsCustomProduct={setIsCustomProduct}
      />
      <ProductPhotos
        images={images}
        setImages={setImages}
        imageFocus={imageFocus}
        setImageFocus={setImageFocus}
      />
      <ProductForm
        formData={formData}
        isFormValid={isFormValid}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        isCustomProduct={isCustomProduct}
        suppliers={suppliers}
      />
      <ToastContainer position="bottom-right" />
    </div>
  );
}

export default AddProduct;
